// Setting → Project Code — ทะเบียนงานทั้งใบจาก Google Sheet
//
// อ่านอย่างเดียว ไม่มีการเขียนกลับ: ชีตยังเป็นที่กรอกงานเหมือนเดิม หน้านี้ทำให้ดูและ
// ค้นได้จากในระบบ โดยไม่ต้องสลับไปเปิดชีตแล้วเลื่อนหาเองใน 1,400 แถว
//
// ข้อมูลถูกแคชไว้ฝั่ง server 10 นาที — ปุ่มดึงข้อมูลใหม่คือทางข้ามแคชนั้นเมื่อมีคนเพิ่ง
// แก้ชีตแล้วอยากเห็นผลทันที · เวลาที่ดึงล่าสุดจึงต้องแสดงไว้เสมอ ไม่งั้นตีความตัวเลข
// ที่เห็นไม่ได้ว่าสดแค่ไหน
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiErrorMessage } from "@shared/api/client";
import { IconRefresh, IconSearch } from "@shared/ui/icons";
import { SearchSelect } from "@shared/ui/search-select";
import { cn } from "@shared/lib/utils";
import { documentFileUrl } from "@features/documents/api/documents-api";
import { fetchProjectRegister, projectCodeKeys } from "../api/settings-api";

/** อายุข้อมูลแบบอ่านง่าย */
function since(iso: string, now: number): string {
  const mins = Math.floor((now - Date.parse(iso)) / 60_000);
  if (!isFinite(mins)) return "";
  if (mins < 1) return "เมื่อครู่นี้";
  if (mins < 60) return `${mins} นาทีก่อน`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs} ชม.ก่อน` : `${Math.floor(hrs / 24)} วันก่อน`;
}

/** สีของป้ายสถานะ — เลขนำหน้าใน Sheet บอกลำดับของงาน (0 ยกเลิก … 6 จบแล้ว) */
function statusTone(status: string): string {
  const n = status.trim()[0];
  if (n === "0") return "is-dead";
  if (n === "2" || n === "3" || n === "4") return "is-live";
  return "";
}

export function ProjectCodePage() {
  const [now] = useState(() => Date.now());
  const [year, setYear] = useState("");
  const [brand, setBrand] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  /** ตั้งปีล่าสุดให้ครั้งเดียวตอนข้อมูลมาถึง — งานที่คนเปิดหน้านี้มาดูคืองานปีปัจจุบัน */
  const [yearSeeded, setYearSeeded] = useState(false);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: projectCodeKeys.all,
    queryFn: () => fetchProjectRegister(false),
    // ไม่หมดอายุเอง — กลับมาหน้านี้ต้องขึ้นทันที การอ่านใหม่เกิดตอนกดปุ่มเท่านั้น
    // (refetch ของปุ่มข้าม staleTime อยู่แล้ว)
    staleTime: Infinity,
    retry: false,
  });

  const rows = data?.rows ?? [];
  const years = [...new Set(rows.map((r) => r.year).filter(Boolean))];
  if (!yearSeeded && years.length) {
    setYearSeeded(true);
    setYear(years[0]);
  }
  const statuses = [...new Set(rows.map((r) => r.status).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
  const brands = [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );

  const needle = q.trim().toLowerCase();
  const shown = rows.filter(
    (r) =>
      (!year || r.year === year) &&
      (!brand || r.brand === brand) &&
      (!status || r.status === status) &&
      (!needle || `${r.code} ${r.brand} ${r.name}`.toLowerCase().includes(needle)),
  );

  /** ดึงใหม่โดยข้ามแคชฝั่ง server — ใช้ query เดิม ไม่สร้าง cache key ใหม่ */
  const hardRefresh = async () => {
    setBusy(true);
    try {
      await fetchProjectRegister(true);
      await refetch();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="breadcrumb">Setting / Project Code</div>
          <h1>Project Code</h1>
          <p className="subtitle">
            ทะเบียนงานจาก Google Sheet · ดูและค้นได้ที่นี่ การแก้ยังทำในชีตเหมือนเดิม
          </p>
        </div>
        <div className="head-actions pc-actions">
          <span className="muted small-note">
            {data?.fetchedAt ? `ดึงข้อมูลล่าสุด ${since(data.fetchedAt, now)}` : ""}
          </span>
          <div className="pc-actions-stack">
            <button
              type="button"
              className="btn-outline pc-refresh"
              disabled={busy || isPending}
              onClick={() => void hardRefresh()}
            >
              <IconRefresh size={15} /> {busy ? "กำลังดึง…" : "ดึงข้อมูลใหม่"}
            </button>
            {/* ลิงก์มาจาก server ไม่ใช่ค่าคงที่ในหน้านี้ — เปลี่ยนไฟล์ที่ env แล้วลิงก์ตามเอง */}
            {data?.sheetUrl ? (
              <a
                className="muted small-note pc-sheet-link"
                href={data.sheetUrl}
                target="_blank"
                rel="noreferrer noopener"
              >
                เปิด Google Sheet ↗
              </a>
            ) : null}
          </div>
        </div>
      </div>

      {isPending ? (
        <div className="panel">
          <p className="muted">กำลังอ่านทะเบียนงานจาก Sheet…</p>
        </div>
      ) : isError ? (
        <div className="panel empty-state">
          <h3>อ่านทะเบียนงานไม่สำเร็จ</h3>
          <p className="muted">{apiErrorMessage(error)}</p>
          <button type="button" className="btn-primary inline-btn" onClick={() => void refetch()}>
            ลองอีกครั้ง
          </button>
        </div>
      ) : (
        <div className="panel">
          <div className="pc-filters">
            <div className="search-box grow-box">
              <IconSearch />
              <input
                type="text"
                placeholder="ค้นหารหัส แบรนด์ หรือชื่องาน…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <label className="camp-select-wrap">
              <span className="muted camp-select-lbl">ปี</span>
              <select
                className="camp-select"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                <option value="">ทุกปี</option>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            <label className="camp-select-wrap">
              <span className="muted camp-select-lbl">สถานะ</span>
              <select
                className="camp-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">ทุกสถานะ</option>
                {statuses.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </label>
            {/* แบรนด์มี 300 กว่าค่า — dropdown ธรรมดากวาดตาหาไม่ไหว */}
            <div className="pc-brand">
              <SearchSelect
                options={[
                  { value: "", label: "ทุกแบรนด์" },
                  ...brands.map((b) => ({ value: b, label: b })),
                ]}
                value={brand}
                onChange={setBrand}
                placeholder="ทุกแบรนด์"
              />
            </div>
            <span className="muted small-note pc-count">
              {shown.length.toLocaleString()}/{rows.length.toLocaleString()} งาน
            </span>
          </div>

          <div className="pc-wrap">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>ปี</th>
                  <th>รหัส</th>
                  <th>แบรนด์</th>
                  <th>ชื่องาน</th>
                  <th>MasterPlan</th>
                  <th>Monthly</th>
                  <th>สถานะ</th>
                  <th>Planner</th>
                  <th>Media Hybrid</th>
                  <th>KOL</th>
                  <th>Biddable</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={`${r.year}-${r.no}-${r.name}`}>
                    <td>{r.year}</td>
                    <td>
                      <span className="code-chip">{r.code || "—"}</span>
                    </td>
                    <td>{r.brand}</td>
                    <td className="pc-name">{r.name}</td>
                    <td className="pc-plan">
                      {/* ✓ ต่อหนึ่งไฟล์ — กดแล้วเปิดไฟล์จริง · ไม่มีไฟล์ก็ไม่ต้องมีอะไรให้กด */}
                      {r.masterPlans?.length ? (
                        r.masterPlans.map((f) => (
                          <a
                            key={f.id}
                            className="pc-plan-ok"
                            href={documentFileUrl(f.id)}
                            target="_blank"
                            rel="noreferrer noopener"
                            title={`เปิด ${f.name}`}
                          >
                            ✓
                          </a>
                        ))
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="pc-plan">
                      {/* ติ๊กที่นี่ไม่ได้ — มาจากแคมเปญในหน้า config ของแบรนด์ ซึ่งเป็นที่เดียว
                          ที่รู้ว่างานนี้คือคอนเทนต์รายเดือน · หน้านี้เป็นทะเบียนอ่านอย่างเดียว */}
                      {r.monthlyContent ? (
                        <span
                          className="pc-plan-ok"
                          title="เป็น Monthly Content — ไม่ต้องมี Master Plan"
                        >
                          ✓
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {r.status ? (
                        <span className={cn("pf-status", statusTone(r.status))}>{r.status}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="pc-people">{r.planner.join(", ") || "—"}</td>
                    <td className="pc-people">{r.hybrid || "—"}</td>
                    <td className="pc-people">{r.kolBuyer.join(", ") || "—"}</td>
                    <td className="pc-people">{r.biddable.join(", ") || "—"}</td>
                  </tr>
                ))}
                {!shown.length && (
                  <tr>
                    <td colSpan={11} className="pc-empty">
                      <span className="muted">ไม่พบงานที่ตรงกับที่กรอง</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
